export type GuideLocale = "en" | "pt-BR" | "es";

export type GuideContent = {
  locale: GuideLocale;
  path: string;
  title: string;
  description: string;
  eyebrow: string;
  intro: string;
  sections: {
    heading: string;
    paragraphs: string[];
    bullets?: string[];
  }[];
  faq: { question: string; answer: string }[];
};

const guides: GuideContent[] = [
  {
    locale: "en",
    path: "/instagram-follow-notifications/",
    title: "How to Get Notifications for New Instagram Follows",
    description:
      "Learn how Instagram follow notifications work and how to monitor new follows on public profiles without sharing your password.",
    eyebrow: "Instagram follow notifications",
    intro:
      "Instagram does not provide a native alert for every new account someone follows. If you need to notice changes over time, you need a consistent way to compare public profile activity.",
    sections: [
      {
        heading: "Why Instagram does not send every follow alert",
        paragraphs: [
          "Instagram can send notifications for interactions with your own account, but it does not offer a general-purpose notification for another public profile's new follows. The old Following Activity feed was also removed.",
        ],
      },
      {
        heading: "What a follow-monitoring tool should do",
        paragraphs: [
          "A useful tracker records a baseline, checks the public profile periodically, and presents changes in chronological order. It should make clear what is visible publicly and should never ask for an Instagram password.",
        ],
        bullets: [
          "Monitor public profiles only",
          "Show the account and time associated with each detected change",
          "Send an alert when a new change is detected",
          "Allow you to stop monitoring at any time",
        ],
      },
      {
        heading: "How Stalkie handles new follow alerts",
        paragraphs: [
          "With Stalkie, you enter a public username and receive a timeline of detected follow and unfollow changes. The service does not connect to your Instagram account, so your credentials are never needed.",
        ],
      },
    ],
    faq: [
      {
        question: "Can Instagram notify me when someone follows a new account?",
        answer:
          "Instagram does not provide a general notification for every new follow made by another profile. A monitoring service can compare public activity over time instead.",
      },
      {
        question: "Do I need to share my Instagram password?",
        answer:
          "No. Stalkie is designed for public profiles and does not require your Instagram login or password.",
      },
    ],
  },
  {
    locale: "en",
    path: "/instagram-unfollow-tracker/",
    title: "Instagram Unfollow Tracker: What You Can Actually See",
    description:
      "Understand how Instagram unfollow tracking works, what public data can reveal, and how to monitor changes without an Instagram login.",
    eyebrow: "Instagram unfollow tracker",
    intro:
      "An unfollow is a change between two snapshots of a public profile. Instagram does not keep a public history of every unfollow, so reliable tracking depends on recording changes consistently.",
    sections: [
      {
        heading: "Why unfollows are difficult to verify manually",
        paragraphs: [
          "A following list can change at any time, and manually checking it gives you no dependable timestamp. It is also easy to miss a brief change or confuse a deactivated, private, or renamed account with an unfollow.",
        ],
      },
      {
        heading: "How an unfollow tracker works",
        paragraphs: [
          "The tracker saves a view of the public following list, checks it again later, and compares the two versions. A change is reported only as a difference between those observations; it should not be presented as access to private Instagram data.",
        ],
        bullets: [
          "Start with a public username",
          "Compare successive public snapshots",
          "Review changes in a dated timeline",
          "Treat private or unavailable profiles as unresolved changes",
        ],
      },
      {
        heading: "Using Stalkie to follow changes",
        paragraphs: [
          "Stalkie turns detected follow and unfollow changes into a readable timeline and can send alerts for new activity. It does not require access to your Instagram account or the target's password.",
        ],
      },
    ],
    faq: [
      {
        question: "Can an unfollow tracker see private activity?",
        answer:
          "No. A responsible tracker should limit itself to information available from public profiles and should not bypass privacy settings.",
      },
      {
        question:
          "Does an unfollow always mean someone intentionally removed an account?",
        answer:
          "Not necessarily. Deactivation, blocking, username changes, and temporary unavailability can also affect what appears in a public list.",
      },
    ],
  },
  {
    locale: "en",
    path: "/instagram-following-list-changes/",
    title: "How to Track Changes in an Instagram Following List",
    description:
      "A practical guide to tracking changes in a public Instagram following list and reviewing new follows in a dated timeline.",
    eyebrow: "Instagram following list changes",
    intro:
      "A following list is a moving snapshot, not an activity log. To understand what changed, you need to compare observations instead of relying on memory or a single visit to a profile.",
    sections: [
      {
        heading: "What a following-list change means",
        paragraphs: [
          "A new account in the list may indicate a follow, while a missing account may indicate an unfollow or a profile that is no longer publicly available. Good reporting should preserve that distinction and show when the change was observed.",
        ],
      },
      {
        heading: "A simple way to monitor a public list",
        paragraphs: [
          "Choose the public profile, create a baseline, and review later snapshots for additions and removals. Automated monitoring makes this practical when changes matter but checking manually every day does not.",
        ],
        bullets: [
          "Use a public username, not private credentials",
          "Record the first observation as a baseline",
          "Separate new follows from unfollows",
          "Keep the timeline easy to scan",
        ],
      },
      {
        heading: "Reviewing list changes with Stalkie",
        paragraphs: [
          "Stalkie monitors public profiles and organizes detected changes by date. You can search a username, review the profile preview, and receive alerts when new activity appears.",
        ],
      },
    ],
    faq: [
      {
        question: "Does Instagram show a history of following-list changes?",
        answer:
          "No. Instagram does not provide a complete public history for another person's following-list changes, which is why comparisons over time are useful.",
      },
      {
        question: "Can I track any Instagram account?",
        answer:
          "Only public profiles can be monitored. Private profiles and unavailable data should remain private.",
      },
    ],
  },
  {
    locale: "pt-BR",
    path: "/pt/notificacoes-de-novos-seguidos-instagram/",
    title: "Como Receber Notificações de Novos Seguidos no Instagram",
    description:
      "Entenda como funcionam as notificações de novos follows e como acompanhar perfis públicos sem compartilhar sua senha do Instagram.",
    eyebrow: "Notificações de novos seguidos",
    intro:
      "O Instagram não oferece um alerta nativo para cada nova conta que alguém começa a seguir. Para acompanhar mudanças ao longo do tempo, é preciso comparar a atividade pública de forma consistente.",
    sections: [
      {
        heading: "Por que o Instagram não envia todo alerta de follow",
        paragraphs: [
          "O Instagram pode enviar notificações sobre interações com a sua conta, mas não oferece uma notificação geral para cada novo follow feito por outro perfil público. A antiga aba de atividade de seguidos também foi removida.",
        ],
      },
      {
        heading: "O que uma ferramenta de monitoramento deve fazer",
        paragraphs: [
          "Um bom rastreador cria uma referência inicial, verifica o perfil público periodicamente e mostra as mudanças em ordem cronológica. Ele deve deixar claro o que é público e nunca pedir sua senha do Instagram.",
        ],
        bullets: [
          "Monitorar somente perfis públicos",
          "Mostrar o perfil e o momento da mudança detectada",
          "Enviar um alerta quando surgir nova atividade",
          "Permitir interromper o monitoramento quando quiser",
        ],
      },
      {
        heading: "Como o Stalkie envia alertas de novos follows",
        paragraphs: [
          "No Stalkie, você informa um nome de usuário público e recebe uma linha do tempo com mudanças de follow e unfollow. O serviço não se conecta à sua conta do Instagram e não precisa das suas credenciais.",
        ],
      },
    ],
    faq: [
      {
        question: "O Instagram avisa quando alguém segue uma conta nova?",
        answer:
          "O Instagram não oferece uma notificação geral para cada novo follow feito por outro perfil. Um serviço de monitoramento pode comparar a atividade pública ao longo do tempo.",
      },
      {
        question: "Preciso compartilhar minha senha do Instagram?",
        answer:
          "Não. O Stalkie foi criado para perfis públicos e não exige login ou senha do Instagram.",
      },
    ],
  },
  {
    locale: "pt-BR",
    path: "/pt/rastreador-de-unfollows-instagram/",
    title: "Rastreador de Unfollows no Instagram: O Que é Possível Ver",
    description:
      "Entenda como funciona o rastreamento de unfollows no Instagram e como acompanhar mudanças sem fazer login na sua conta.",
    eyebrow: "Rastreador de unfollows",
    intro:
      "Um unfollow é uma mudança entre duas observações de um perfil público. O Instagram não mantém um histórico público de cada unfollow, então o acompanhamento depende de registrar as mudanças de forma consistente.",
    sections: [
      {
        heading: "Por que é difícil confirmar unfollows manualmente",
        paragraphs: [
          "A lista de seguidos pode mudar a qualquer momento e uma verificação manual não oferece um horário confiável. Também é fácil confundir uma conta desativada, privada ou renomeada com um unfollow.",
        ],
      },
      {
        heading: "Como funciona um rastreador de unfollows",
        paragraphs: [
          "O rastreador salva uma visão da lista pública, verifica novamente depois e compara as duas versões. A mudança deve ser apresentada apenas como uma diferença observada, sem sugerir acesso a dados privados.",
        ],
        bullets: [
          "Começar com um nome de usuário público",
          "Comparar observações públicas sucessivas",
          "Revisar as mudanças em uma linha do tempo",
          "Tratar perfis privados ou indisponíveis como mudanças não confirmadas",
        ],
      },
      {
        heading: "Acompanhe mudanças com o Stalkie",
        paragraphs: [
          "O Stalkie transforma mudanças detectadas de follow e unfollow em uma linha do tempo simples e pode enviar alertas de novas atividades. Ele não exige acesso à sua conta nem à senha do perfil monitorado.",
        ],
      },
    ],
    faq: [
      {
        question: "Um rastreador de unfollows consegue ver atividade privada?",
        answer:
          "Não. Um rastreador responsável deve se limitar às informações disponíveis em perfis públicos e não contornar configurações de privacidade.",
      },
      {
        question:
          "Todo unfollow significa que alguém removeu outra conta de propósito?",
        answer:
          "Não necessariamente. Desativação, bloqueio, mudança de nome de usuário e indisponibilidade temporária também podem alterar o que aparece em uma lista pública.",
      },
    ],
  },
  {
    locale: "pt-BR",
    path: "/pt/mudancas-na-lista-de-seguidos-instagram/",
    title: "Como Acompanhar Mudanças na Lista de Seguidos do Instagram",
    description:
      "Um guia prático para acompanhar mudanças em uma lista pública de seguidos do Instagram e revisar novos follows por data.",
    eyebrow: "Mudanças na lista de seguidos",
    intro:
      "Uma lista de seguidos é uma fotografia que muda com o tempo, não um registro de atividades. Para entender o que mudou, é melhor comparar observações do que confiar na memória ou em uma única visita ao perfil.",
    sections: [
      {
        heading: "O que significa uma mudança na lista",
        paragraphs: [
          "Uma nova conta na lista pode indicar um follow, enquanto uma conta que desapareceu pode indicar um unfollow ou um perfil que deixou de estar disponível publicamente. Um bom relatório deve diferenciar essas possibilidades e mostrar quando a mudança foi observada.",
        ],
      },
      {
        heading: "Uma forma simples de monitorar uma lista pública",
        paragraphs: [
          "Escolha o perfil público, crie uma referência inicial e compare observações posteriores para encontrar adições e remoções. O monitoramento automático torna isso viável quando acompanhar manualmente todos os dias não é prático.",
        ],
        bullets: [
          "Usar um nome de usuário público, sem credenciais",
          "Registrar a primeira observação como referência",
          "Separar novos follows de unfollows",
          "Manter a linha do tempo fácil de consultar",
        ],
      },
      {
        heading: "Revise mudanças com o Stalkie",
        paragraphs: [
          "O Stalkie monitora perfis públicos e organiza as mudanças detectadas por data. Você pode pesquisar um nome de usuário, revisar a prévia do perfil e receber alertas quando surgir nova atividade.",
        ],
      },
    ],
    faq: [
      {
        question:
          "O Instagram mostra um histórico de mudanças na lista de seguidos?",
        answer:
          "Não. O Instagram não oferece um histórico público completo das mudanças na lista de outra pessoa, por isso as comparações ao longo do tempo são úteis.",
      },
      {
        question: "Posso acompanhar qualquer conta do Instagram?",
        answer:
          "Somente perfis públicos podem ser monitorados. Perfis privados e dados indisponíveis devem continuar privados.",
      },
    ],
  },
  {
    locale: "es",
    path: "/es/notificaciones-nuevos-seguidos-instagram/",
    title: "Cómo Recibir Notificaciones de Nuevos Follows en Instagram",
    description:
      "Entiende cómo funcionan las notificaciones de nuevos follows y cómo monitorear perfiles públicos sin compartir tu contraseña de Instagram.",
    eyebrow: "Notificaciones de nuevos follows",
    intro:
      "Instagram no ofrece una alerta nativa para cada cuenta nueva que alguien empieza a seguir. Para detectar cambios con el tiempo, necesitas comparar la actividad pública de forma constante.",
    sections: [
      {
        heading: "Por qué Instagram no envía cada alerta de follow",
        paragraphs: [
          "Instagram puede enviar notificaciones sobre interacciones con tu cuenta, pero no ofrece una alerta general para cada nuevo follow realizado por otro perfil público. Además, eliminó la antigua pestaña de actividad de seguidos.",
        ],
      },
      {
        heading: "Qué debe hacer una herramienta de monitoreo",
        paragraphs: [
          "Un buen rastreador crea una referencia inicial, revisa el perfil público periódicamente y muestra los cambios en orden cronológico. Debe explicar qué datos son públicos y nunca pedir tu contraseña de Instagram.",
        ],
        bullets: [
          "Monitorear solo perfiles públicos",
          "Mostrar la cuenta y el momento del cambio detectado",
          "Enviar una alerta cuando aparece nueva actividad",
          "Permitir detener el monitoreo cuando quieras",
        ],
      },
      {
        heading: "Cómo Stalkie gestiona las alertas de nuevos follows",
        paragraphs: [
          "En Stalkie, introduces un nombre de usuario público y recibes una línea de tiempo con cambios de follow y unfollow. El servicio no se conecta a tu cuenta de Instagram ni necesita tus credenciales.",
        ],
      },
    ],
    faq: [
      {
        question: "¿Instagram avisa cuando alguien sigue una cuenta nueva?",
        answer:
          "Instagram no ofrece una notificación general para cada nuevo follow realizado por otro perfil. Un servicio de monitoreo puede comparar la actividad pública con el tiempo.",
      },
      {
        question: "¿Tengo que compartir mi contraseña de Instagram?",
        answer:
          "No. Stalkie está diseñado para perfiles públicos y no requiere tu inicio de sesión ni tu contraseña de Instagram.",
      },
    ],
  },
  {
    locale: "es",
    path: "/es/rastreador-de-unfollows-instagram/",
    title: "Rastreador de Unfollows en Instagram: Qué Puedes Ver",
    description:
      "Entiende cómo funciona el seguimiento de unfollows en Instagram y cómo monitorear cambios sin iniciar sesión en tu cuenta.",
    eyebrow: "Rastreador de unfollows",
    intro:
      "Un unfollow es un cambio entre dos observaciones de un perfil público. Instagram no mantiene un historial público de cada unfollow, así que el seguimiento depende de registrar los cambios de forma constante.",
    sections: [
      {
        heading: "Por qué es difícil confirmar unfollows manualmente",
        paragraphs: [
          "La lista de seguidos puede cambiar en cualquier momento y una revisión manual no ofrece una hora confiable. También es fácil confundir una cuenta desactivada, privada o renombrada con un unfollow.",
        ],
      },
      {
        heading: "Cómo funciona un rastreador de unfollows",
        paragraphs: [
          "El rastreador guarda una vista de la lista pública, vuelve a revisarla y compara las dos versiones. El cambio debe presentarse como una diferencia observada, sin sugerir acceso a datos privados.",
        ],
        bullets: [
          "Empezar con un nombre de usuario público",
          "Comparar observaciones públicas sucesivas",
          "Revisar los cambios en una línea de tiempo",
          "Tratar perfiles privados o no disponibles como cambios sin confirmar",
        ],
      },
      {
        heading: "Sigue los cambios con Stalkie",
        paragraphs: [
          "Stalkie convierte los cambios detectados de follow y unfollow en una línea de tiempo clara y puede enviar alertas de nueva actividad. No requiere acceso a tu cuenta ni a la contraseña del perfil monitoreado.",
        ],
      },
    ],
    faq: [
      {
        question: "¿Un rastreador de unfollows puede ver actividad privada?",
        answer:
          "No. Un rastreador responsable debe limitarse a la información disponible en perfiles públicos y no evadir la configuración de privacidad.",
      },
      {
        question:
          "¿Todo unfollow significa que alguien eliminó otra cuenta intencionalmente?",
        answer:
          "No necesariamente. La desactivación, el bloqueo, un cambio de nombre de usuario o una indisponibilidad temporal también pueden cambiar lo que aparece en una lista pública.",
      },
    ],
  },
  {
    locale: "es",
    path: "/es/cambios-en-la-lista-de-seguidos-instagram/",
    title: "Cómo Seguir Cambios en la Lista de Seguidos de Instagram",
    description:
      "Una guía práctica para seguir cambios en una lista pública de seguidos de Instagram y revisar nuevos follows por fecha.",
    eyebrow: "Cambios en la lista de seguidos",
    intro:
      "Una lista de seguidos es una instantánea que cambia con el tiempo, no un registro de actividad. Para entender qué cambió, conviene comparar observaciones en lugar de depender de la memoria o de una sola visita al perfil.",
    sections: [
      {
        heading: "Qué significa un cambio en la lista",
        paragraphs: [
          "Una cuenta nueva puede indicar un follow, mientras que una cuenta que desaparece puede indicar un unfollow o un perfil que ya no está disponible públicamente. Un buen informe debe distinguir estas posibilidades y mostrar cuándo se observó el cambio.",
        ],
      },
      {
        heading: "Una forma sencilla de monitorear una lista pública",
        paragraphs: [
          "Elige el perfil público, crea una referencia inicial y compara observaciones posteriores para encontrar adiciones y eliminaciones. El monitoreo automático lo hace práctico cuando revisar manualmente todos los días no es una opción.",
        ],
        bullets: [
          "Usar un nombre de usuario público, sin credenciales",
          "Registrar la primera observación como referencia",
          "Separar nuevos follows de unfollows",
          "Mantener la línea de tiempo fácil de consultar",
        ],
      },
      {
        heading: "Revisa los cambios con Stalkie",
        paragraphs: [
          "Stalkie monitorea perfiles públicos y organiza los cambios detectados por fecha. Puedes buscar un nombre de usuario, revisar la vista previa del perfil y recibir alertas cuando aparece nueva actividad.",
        ],
      },
    ],
    faq: [
      {
        question:
          "¿Instagram muestra un historial de cambios en la lista de seguidos?",
        answer:
          "No. Instagram no ofrece un historial público completo de los cambios en la lista de otra persona, por eso son útiles las comparaciones a lo largo del tiempo.",
      },
      {
        question: "¿Puedo seguir cualquier cuenta de Instagram?",
        answer:
          "Solo se pueden monitorear perfiles públicos. Los perfiles privados y los datos no disponibles deben permanecer privados.",
      },
    ],
  },
];

type LegacyGuideCopy = Omit<GuideContent, "sections" | "faq"> & {
  sectionHeading: string;
  sectionBody: string;
  bullets: string[];
  faqQuestion: string;
  faqAnswer: string;
  additionalSections?: GuideContent["sections"];
  faqItems?: GuideContent["faq"];
};

const legacyGuideCopies: LegacyGuideCopy[] = [
  {
    locale: "en",
    path: "/how-to-see-who-someone-followed-on-instagram/",
    title: "How to See Who Someone Recently Followed on Instagram",
    description:
      "Learn how to track who someone recently followed on Instagram using public profile activity, without sharing your password.",
    eyebrow: "See recent Instagram follows",
    intro:
      "Instagram removed its Following Activity feed, so there is no native history for another person's new follows. This guide explains what you can check and how public monitoring works.",
    sectionHeading: "Ways to see recent follows",
    sectionBody:
      "Manual checks provide only a momentary view and do not create a reliable history. A monitoring tool can compare public observations over time and organize detected changes in a dated timeline.",
    bullets: [
      "Use a public username only",
      "Compare profile snapshots over time",
      "Review new follows in chronological order",
      "Avoid tools that ask for your Instagram password",
    ],
    faqQuestion: "Can Instagram show who someone recently followed?",
    faqAnswer:
      "Instagram does not provide a complete public history of another person's recent follows. A service such as Stalkie can compare activity from public profiles over time.",
    additionalSections: [
      {
        heading: "A repeatable way to check a public profile",
        paragraphs: [
          "A useful check has a starting point and a later comparison. Choose a public username, note the visible following list, then return after a defined interval so additions and removals can be reviewed as changes instead of guesses.",
        ],
        bullets: [
          "Record what was visible and when you observed it",
          "Compare the same public profile on a later check",
          "Review additions separately from removals",
          "Treat the detection time as the time you observed the change",
        ],
      },
      {
        heading: "What a recent-follow result can and cannot prove",
        paragraphs: [
          "A detected difference shows that two public observations were different. It does not prove the exact moment an account was followed, and an unavailable profile can have more than one explanation.",
        ],
        bullets: [
          "Private-profile activity remains unavailable",
          "A renamed, deactivated, or blocked account may look like a removal",
          "A public snapshot is evidence of an observed change, not private access",
        ],
      },
    ],
    faqItems: [
      {
        question: "How quickly can a new follow appear in a comparison?",
        answer:
          "It depends on when the public profile is observed again. A tracker can report a change after a later observation, but it cannot promise the exact time the follow happened.",
      },
      {
        question: "Can I see recent follows on a private Instagram account?",
        answer:
          "No. A responsible tracker can only use information available from a public profile and should not bypass Instagram privacy settings.",
      },
    ],
  },
  {
    locale: "en",
    path: "/instagram-follow-tracker/",
    title: "Instagram Follow Tracker: How It Works",
    description:
      "Understand how an Instagram follow tracker detects new follows on public profiles without requiring your Instagram login.",
    eyebrow: "Instagram follow tracker",
    intro:
      "A follow tracker compares public profile activity across multiple observations. It helps you notice changes without pretending to access private data or a hidden Instagram history.",
    sectionHeading: "What to look for in a follow tracker",
    sectionBody:
      "A trustworthy tracker should explain its data limits, monitor public profiles, and show detected changes in a timeline. It should never require your Instagram credentials to do a public-profile check.",
    bullets: [
      "Public-profile monitoring",
      "A clear baseline and comparison process",
      "Dated follow and unfollow events",
      "No Instagram password or account connection",
    ],
    faqQuestion: "Does an Instagram follow tracker need my login?",
    faqAnswer:
      "No. Stalkie is designed to monitor public profiles and does not require your Instagram username or password.",
    additionalSections: [
      {
        heading: "How the first check becomes a baseline",
        paragraphs: [
          "The first public-profile check is a baseline, not a complete history. Later checks can be compared with that baseline to identify accounts that appeared or disappeared from the visible following list.",
        ],
        bullets: [
          "Enter a public Instagram username",
          "Save the visible state as the starting point",
          "Run a later observation of the same profile",
          "Review the differences in the activity timeline",
        ],
      },
      {
        heading: "Questions to ask before trusting a result",
        paragraphs: [
          "A follow tracker should describe what it observes, when it observes it, and what it cannot know. Clear limits are more useful than a promise to reveal private Instagram activity.",
        ],
        bullets: [
          "Does it work only with public profiles?",
          "Does it distinguish observation time from action time?",
          "Does it explain unavailable or changed accounts?",
          "Does it avoid asking for an Instagram password?",
        ],
      },
    ],
    faqItems: [
      {
        question: "How does a follow tracker detect a new follow?",
        answer:
          "It compares successive observations of the same public profile and reports accounts that appear as a detected difference. That is different from accessing a private Instagram activity log.",
      },
      {
        question: "What happens when a tracked profile becomes unavailable?",
        answer:
          "The result may be unresolved because a profile can be private, renamed, deactivated, blocked, or temporarily unavailable. A trustworthy tracker should show that limitation instead of guessing.",
      },
    ],
  },
  {
    locale: "en",
    path: "/track-instagram-following-activity/",
    title: "How to Track Instagram Following Activity",
    description:
      "Learn how to track public Instagram following activity and review new follows and unfollows in a clear timeline.",
    eyebrow: "Track Instagram following activity",
    intro:
      "Following activity changes quickly and Instagram does not provide a complete public activity log for another profile. Tracking works by comparing public observations over time.",
    sectionHeading: "How following activity is tracked",
    sectionBody:
      "The process starts with a public profile baseline. Later observations are compared with it, and additions or removals are presented as changes that were detected at a particular time.",
    bullets: [
      "Select a public Instagram profile",
      "Create a baseline of its visible following list",
      "Compare later observations",
      "Review follows and unfollows in one timeline",
    ],
    faqQuestion: "Can I track private Instagram following activity?",
    faqAnswer:
      "No. Only information available from public profiles should be monitored. Private activity and restricted data remain private.",
    additionalSections: [
      {
        heading: "How to read a following-activity timeline",
        paragraphs: [
          "The useful part of a timeline is the comparison context: which public profile was checked, what changed, and when that difference was observed. It should not turn a snapshot into a claim about private intent.",
        ],
        bullets: [
          "Start with a public profile baseline",
          "Separate new follows from removals",
          "Keep the observation date with each detected change",
          "Mark unavailable profiles as unresolved",
        ],
      },
      {
        heading: "Why a single check is not a history",
        paragraphs: [
          "A single visit shows only the current public list. It cannot tell you when an account was added or removed. Repeated observations create a record of detected differences over time.",
        ],
      },
    ],
    faqItems: [
      {
        question: "Does a following-activity tracker show exact action times?",
        answer:
          "No. It can show when a difference was detected between observations. The exact time of the follow or unfollow is not available from a public snapshot alone.",
      },
    ],
  },
  {
    locale: "en",
    path: "/anonymous-instagram-tracker/",
    title: "Anonymous Instagram Tracker: What It Can See",
    description:
      "Learn how anonymous Instagram tracking works for public profiles and why a tracker should never ask for your Instagram password.",
    eyebrow: "Anonymous Instagram tracker",
    intro:
      "Anonymous monitoring means checking public profile activity without connecting your Instagram account or notifying the profile being observed. It does not mean bypassing privacy settings.",
    sectionHeading: "How anonymous monitoring works",
    sectionBody:
      "Stalkie accepts a public username, compares visible activity over time, and presents detected changes in a timeline. Your Instagram credentials are not part of the process.",
    bullets: [
      "No Instagram login required",
      "Public profiles only",
      "Chronological activity timeline",
      "Alerts for newly detected changes",
    ],
    faqQuestion: "Is anonymous Instagram tracking private?",
    faqAnswer:
      "A responsible service should use only public information and should not request or expose Instagram credentials. Private-profile activity cannot be monitored.",
  },
  {
    locale: "pt-BR",
    path: "/pt/como-ver-quem-alguem-seguiu-no-instagram/",
    title: "Como Ver Quem Alguém Seguiu Recentemente no Instagram",
    description:
      "Saiba como acompanhar quem alguém seguiu recentemente usando atividade pública, sem compartilhar sua senha do Instagram.",
    eyebrow: "Ver follows recentes no Instagram",
    intro:
      "O Instagram removeu a aba de Atividade de Seguindo e não oferece um histórico nativo dos novos follows de outra pessoa. Este guia mostra o que é possível verificar em perfis públicos.",
    sectionHeading: "Formas de ver follows recentes",
    sectionBody:
      "Uma verificação manual mostra apenas um momento e não cria um histórico confiável. Uma ferramenta de monitoramento compara observações públicas ao longo do tempo e organiza as mudanças por data.",
    bullets: [
      "Usar somente um nome de usuário público",
      "Comparar observações do perfil ao longo do tempo",
      "Revisar novos follows em ordem cronológica",
      "Evitar ferramentas que pedem sua senha do Instagram",
    ],
    faqQuestion: "O Instagram mostra quem alguém seguiu recentemente?",
    faqAnswer:
      "O Instagram não oferece um histórico público completo dos follows recentes de outra pessoa. O Stalkie compara a atividade de perfis públicos ao longo do tempo.",
  },
  {
    locale: "pt-BR",
    path: "/pt/rastreador-de-seguidores-instagram/",
    title: "Rastreador de Seguidores do Instagram: Como Funciona",
    description:
      "Entenda como um rastreador de seguidores detecta novos follows em perfis públicos sem exigir seu login do Instagram.",
    eyebrow: "Rastreador de seguidores do Instagram",
    intro:
      "Um rastreador compara a atividade pública de um perfil em diferentes momentos. Assim, ajuda a perceber mudanças sem fingir que acessa dados privados ou um histórico oculto do Instagram.",
    sectionHeading: "O que procurar em um rastreador",
    sectionBody:
      "Um rastreador confiável explica seus limites, monitora perfis públicos e mostra as mudanças em uma linha do tempo. Ele nunca deve exigir suas credenciais do Instagram para consultar um perfil público.",
    bullets: [
      "Monitoramento de perfis públicos",
      "Processo claro de referência e comparação",
      "Eventos de follow e unfollow com data",
      "Sem senha ou conexão com sua conta do Instagram",
    ],
    faqQuestion: "Um rastreador de seguidores precisa do meu login?",
    faqAnswer:
      "Não. O Stalkie foi criado para monitorar perfis públicos e não exige seu usuário ou senha do Instagram.",
  },
  {
    locale: "pt-BR",
    path: "/pt/rastrear-atividade-de-seguidos-instagram/",
    title: "Como Rastrear a Atividade de Seguidos no Instagram",
    description:
      "Saiba como acompanhar a atividade pública de seguidos no Instagram e revisar novos follows e unfollows em uma linha do tempo.",
    eyebrow: "Rastrear atividade de seguidos",
    intro:
      "A atividade de seguidos muda rapidamente e o Instagram não oferece um registro público completo de outro perfil. O acompanhamento funciona comparando observações públicas ao longo do tempo.",
    sectionHeading: "Como a atividade é acompanhada",
    sectionBody:
      "O processo começa com uma referência de um perfil público. Observações posteriores são comparadas, e adições ou remoções aparecem como mudanças detectadas em determinado momento.",
    bullets: [
      "Escolher um perfil público",
      "Criar uma referência da lista visível de seguidos",
      "Comparar observações posteriores",
      "Revisar follows e unfollows em uma única linha do tempo",
    ],
    faqQuestion: "Posso rastrear a atividade de seguidos de um perfil privado?",
    faqAnswer:
      "Não. Somente informações disponíveis em perfis públicos devem ser monitoradas. Atividades privadas e dados restritos continuam privados.",
  },
  {
    locale: "pt-BR",
    path: "/pt/rastreador-anonimo-instagram/",
    title: "Rastreador Anônimo de Instagram: O Que Ele Pode Ver",
    description:
      "Entenda como funciona o monitoramento anônimo de perfis públicos e por que um rastreador nunca deve pedir sua senha do Instagram.",
    eyebrow: "Rastreador anônimo de Instagram",
    intro:
      "Monitoramento anônimo significa acompanhar atividade pública sem conectar sua conta do Instagram ou avisar o perfil observado. Isso não significa contornar configurações de privacidade.",
    sectionHeading: "Como funciona o monitoramento anônimo",
    sectionBody:
      "O Stalkie recebe um nome de usuário público, compara a atividade visível ao longo do tempo e apresenta as mudanças detectadas em uma linha do tempo. Suas credenciais não fazem parte do processo.",
    bullets: [
      "Sem login do Instagram",
      "Somente perfis públicos",
      "Linha do tempo cronológica",
      "Alertas de novas mudanças detectadas",
    ],
    faqQuestion: "O rastreamento anônimo de Instagram é privado?",
    faqAnswer:
      "Um serviço responsável deve usar apenas informações públicas e não pedir ou expor credenciais do Instagram. A atividade de perfis privados não pode ser monitorada.",
  },
  {
    locale: "es",
    path: "/es/como-ver-a-quien-alguien-siguio-en-instagram/",
    title: "Cómo Ver a Quién Alguien Siguió Recientemente en Instagram",
    description:
      "Aprende a seguir a quién alguien siguió recientemente usando actividad pública, sin compartir tu contraseña de Instagram.",
    eyebrow: "Ver follows recientes en Instagram",
    intro:
      "Instagram eliminó la pestaña de Actividad de Seguimiento y no ofrece un historial nativo de los nuevos follows de otra persona. Esta guía explica qué puedes comprobar en perfiles públicos.",
    sectionHeading: "Formas de ver follows recientes",
    sectionBody:
      "Una revisión manual muestra solo un momento y no crea un historial fiable. Una herramienta de monitoreo compara observaciones públicas con el tiempo y organiza los cambios por fecha.",
    bullets: [
      "Usar solo un nombre de usuario público",
      "Comparar observaciones del perfil con el tiempo",
      "Revisar nuevos follows en orden cronológico",
      "Evitar herramientas que pidan tu contraseña de Instagram",
    ],
    faqQuestion: "¿Instagram muestra a quién alguien siguió recientemente?",
    faqAnswer:
      "Instagram no ofrece un historial público completo de los follows recientes de otra persona. Stalkie compara la actividad de perfiles públicos con el tiempo.",
  },
  {
    locale: "es",
    path: "/es/rastreador-de-seguidores-instagram/",
    title: "Rastreador de Seguidores de Instagram: Cómo Funciona",
    description:
      "Entiende cómo un rastreador de seguidores detecta nuevos follows en perfiles públicos sin pedir tu inicio de sesión de Instagram.",
    eyebrow: "Rastreador de seguidores de Instagram",
    intro:
      "Un rastreador compara la actividad pública de un perfil en distintos momentos. Así ayuda a notar cambios sin fingir que accede a datos privados o a un historial oculto de Instagram.",
    sectionHeading: "Qué buscar en un rastreador",
    sectionBody:
      "Un rastreador confiable explica sus límites, monitorea perfiles públicos y muestra los cambios en una línea de tiempo. Nunca debería pedir tus credenciales de Instagram para consultar un perfil público.",
    bullets: [
      "Monitoreo de perfiles públicos",
      "Proceso claro de referencia y comparación",
      "Eventos de follow y unfollow con fecha",
      "Sin contraseña ni conexión con tu cuenta",
    ],
    faqQuestion: "¿Un rastreador de seguidores necesita mi inicio de sesión?",
    faqAnswer:
      "No. Stalkie está diseñado para monitorear perfiles públicos y no requiere tu usuario ni contraseña de Instagram.",
  },
  {
    locale: "es",
    path: "/es/rastrear-actividad-de-seguidos-instagram/",
    title: "Cómo Rastrear la Actividad de Seguidos en Instagram",
    description:
      "Aprende a seguir la actividad pública de seguidos en Instagram y revisar nuevos follows y unfollows en una línea de tiempo clara.",
    eyebrow: "Rastrear actividad de seguidos",
    intro:
      "La actividad de seguidos cambia rápidamente y Instagram no ofrece un registro público completo de otro perfil. El seguimiento funciona comparando observaciones públicas a lo largo del tiempo.",
    sectionHeading: "Cómo se sigue la actividad",
    sectionBody:
      "El proceso comienza con una referencia de un perfil público. Las observaciones posteriores se comparan, y las adiciones o eliminaciones aparecen como cambios detectados en un momento concreto.",
    bullets: [
      "Elegir un perfil público",
      "Crear una referencia de su lista visible de seguidos",
      "Comparar observaciones posteriores",
      "Revisar follows y unfollows en una sola línea de tiempo",
    ],
    faqQuestion:
      "¿Puedo rastrear la actividad de seguidos de un perfil privado?",
    faqAnswer:
      "No. Solo debe monitorearse la información disponible en perfiles públicos. La actividad privada y los datos restringidos siguen siendo privados.",
  },
  {
    locale: "es",
    path: "/es/rastreador-anonimo-instagram/",
    title: "Rastreador Anónimo de Instagram: Qué Puede Ver",
    description:
      "Entiende cómo funciona el monitoreo anónimo de perfiles públicos y por qué un rastreador nunca debe pedir tu contraseña de Instagram.",
    eyebrow: "Rastreador anónimo de Instagram",
    intro:
      "El monitoreo anónimo significa seguir actividad pública sin conectar tu cuenta de Instagram ni avisar al perfil observado. No significa evadir la configuración de privacidad.",
    sectionHeading: "Cómo funciona el monitoreo anónimo",
    sectionBody:
      "Stalkie recibe un nombre de usuario público, compara la actividad visible con el tiempo y presenta los cambios detectados en una línea de tiempo. Tus credenciales no forman parte del proceso.",
    bullets: [
      "Sin inicio de sesión de Instagram",
      "Solo perfiles públicos",
      "Línea de tiempo cronológica",
      "Alertas de nuevos cambios detectados",
    ],
    faqQuestion: "¿El rastreo anónimo de Instagram es privado?",
    faqAnswer:
      "Un servicio responsable debe usar solo información pública y no pedir ni exponer credenciales de Instagram. La actividad de perfiles privados no puede monitorearse.",
  },
];

const legacyGuides: GuideContent[] = legacyGuideCopies.map((guide) => ({
  locale: guide.locale,
  path: guide.path,
  title: guide.title,
  description: guide.description,
  eyebrow: guide.eyebrow,
  intro: guide.intro,
  sections: [
    {
      heading: guide.sectionHeading,
      paragraphs: [guide.sectionBody],
      bullets: guide.bullets,
    },
    ...(guide.additionalSections ?? []),
    {
      heading:
        guide.locale === "pt-BR"
          ? "Como o Stalkie ajuda"
          : guide.locale === "es"
            ? "Cómo ayuda Stalkie"
            : "How Stalkie helps",
      paragraphs: [
        guide.locale === "pt-BR"
          ? "O Stalkie monitora perfis públicos e organiza as mudanças detectadas por data, sem exigir login do Instagram."
          : guide.locale === "es"
            ? "Stalkie monitorea perfiles públicos y organiza los cambios detectados por fecha, sin pedir el inicio de sesión de Instagram."
            : "Stalkie monitors public profiles and organizes detected changes by date without requiring an Instagram login.",
      ],
    },
  ],
  faq: [
    { question: guide.faqQuestion, answer: guide.faqAnswer },
    ...(guide.faqItems ?? []),
  ],
}));

const allGuides = [...guides, ...legacyGuides];

export const guideContentByPath = Object.fromEntries(
  allGuides.map((guide) => [guide.path, guide]),
) as Record<string, GuideContent>;

export const allGuidePaths = allGuides.map((guide) => guide.path);
